import { SpaceType } from "@components/atoms/Spacer/types";
import React, {
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { useTheme } from "styled-components/native";
import { Platform, Pressable } from "react-native";
import { SelectionType } from "@components/atoms/Filters/types";
import ImageTextFilterItem from "@components/atoms/Filters/ImageTextListSection/ImageTextFilterItem";
import ShowMoreButton from "@components/atoms/Filters/ShowMoreButton";
import { VDBText } from "@components/atoms";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useDispatch } from "react-redux";
import {
  removeFiltersPayload,
  setFiltersPayload,
  setExcludePayload,
  resetExcludePayloadByFilterType,
  useSearchFiltersSlice,
  useValueFromPayload,
} from "@redux/slices/searchFilters";
import SelectedItemsTitle from "@components/atoms/Filters/SelectedItemsTitle";
import { SelectedItemTheme } from "@components/atoms/Button/types";
import useResetFilter from "../../../../hooks/useResetFilter";
import useRemoveColorFilters from "../FilterHooks/UseRemoveColorFilters";
import { FilterScreenContext } from "@scenes/explore/FilterScreenContext";
import { ProductCategory, screenWidth } from "@utils/constants";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { normalizeBoolean } from "../ButtonSection/Utils";
import { useUserInfo } from "@redux/slices/userInfoSlice";

const getUniqueSearchPayloads = (options: any[] = []) => {
  const counts = new Map<string, number>();
  options.forEach((option) => {
    option?.search_payload?.forEach((payload: string) => {
      counts.set(payload, (counts.get(payload) || 0) + 1);
    });
  });

  return options.map(
    (option) =>
      option?.search_payload?.filter(
        (payload: string) => counts.get(payload) === 1
      ) || []
  );
};

const isOptionSelectedFromValue = (
  option: any,
  value: any,
  uniquePayloads: string[]
) => {
  if (value == null || !option) return false;
  const values = Array.isArray(value) ? value : [value];
  if (option.name && values.includes(option.name)) return true;
  return uniquePayloads.some((payload) => values.includes(payload));
};

type ImageTextListSectionProps = {
  options: Array<any>;
  columns: number;
  selectionType?: string;
  displayedRows?: number;
  sectionTitle?: string;
  payloadName?: Array<string>;
  value?: any[];
  sectionName?: string;
  parentFilter?: string;
  selectedStyle: SelectedItemTheme;
  minWidth?: number;
  selectedProduct: ProductCategory;
  enterSecondFlow?: boolean;
  isCollapsible?: boolean;
};

const ImageTextListSection = ({
  options = [],
  selectionType: mSelectionType = SelectionType.multiple,
  displayedRows = 2,
  sectionTitle = "",
  payloadName = [],
  sectionName = "",
  parentFilter = "",
  selectedStyle,
  minWidth = 72,
  selectedProduct = ProductCategory.DIAMOND,
  columns = 2,
  enterSecondFlow = false,
  isCollapsible,
}: ImageTextListSectionProps) => {
  const userInfo = useUserInfo();
  const mEnterSecondFlow = normalizeBoolean(enterSecondFlow);
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const { jcSelectedOption } = useJewelryConfiguratorState();
  const selectedShape =
    jcSelectedOption?.current?.option_value?.supported_shapes;
  const isShapeFilter = sectionTitle === "Shape";
  const { auxPayload: filtersPayload, min_max_Options } =
    useSearchFiltersSlice();
  const modifyShapeSupportForBuilder =
    min_max_Options?.supported_shapes?.length > 0 &&
    userInfo.user.feature_settings?.allow_search_modify_in_builder &&
    enableJewelryBuilder &&
    mEnterSecondFlow;
  const selectionType = modifyShapeSupportForBuilder
    ? SelectionType.single
    : mSelectionType;
  const basePayloadKey = Array.isArray(payloadName)
    ? payloadName?.[0]
    : payloadName;
  const baseSectionKey =
    basePayloadKey || sectionTitle || sectionName || "options";
  const safeKey = (value: unknown) =>
    String(value ?? "item")
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9._-]/g, "");
  const baseTestId = `filters.${
    sectionName?.toLowerCase()?.replaceAll(" ", "-")?.toString() || "section"
  }.${safeKey(baseSectionKey)}.imageTextList`;
  const mOptions = React.useMemo(() => {
    let filtered = options;
    if (modifyShapeSupportForBuilder) {
      filtered = filtered.filter((option) =>
        min_max_Options.supported_shapes.includes(option.name)
      );
    } else if (enableJewelryBuilder && mEnterSecondFlow && isShapeFilter) {
      filtered = filtered.filter((option) => option.name === selectedShape);
    }

    return filtered;
  }, [
    options,
    enableJewelryBuilder,
    mEnterSecondFlow,
    isShapeFilter,
    selectedShape,
    min_max_Options?.supported_shapes,
  ]);

  const typography = useTypographyStore();
  const theme = useTheme();
  const dispatch = useDispatch();
  const value = useValueFromPayload(sectionName, payloadName);
  const isTouched = useRef(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const selected = useMemo(() => {
    const uniquePayloadsByIndex = getUniqueSearchPayloads(mOptions);
    return (
      mOptions?.filter((option, index) =>
        isOptionSelectedFromValue(
          option,
          value,
          uniquePayloadsByIndex[index] || []
        )
      ) || []
    );
  }, [mOptions, value]);

  const { containerWidth, fromResultScreen } = useContext(FilterScreenContext);
  const [selectedItems, setSelectedItems] = useState<any[]>(selected || []);

  useEffect(() => {
    if (isTouched.current) return;
    setSelectedItems(selected);
  }, [selected]);
  useResetFilter(() => {
    setSelectedItems([]);
  });
  const [showAll, setShowAll] = useState<boolean>(!displayedRows);
  const [startRangeIndex, setStartRangeIndex] = useState<number | null>(null);
  const [parentValue, setParentValue] = useState(null);

  useRemoveColorFilters(sectionName);

  useEffect(() => {
    const parentFilterValue = parentFilter
      ? filtersPayload?.[sectionName]?.[parentFilter]
      : null;

    const selectedNestedValue = mOptions
      ?.find((o) => o?.name === parentFilterValue)
      ?.options?.filter((o: any) => value?.includes(o.search_payload?.[0]));

    if (selectedNestedValue?.length > 0) setSelectedItems(selectedNestedValue);

    if (parentFilterValue !== parentValue) {
      setParentValue(parentFilterValue);
    }
  }, [parentFilter, filtersPayload?.[sectionName]?.[parentFilter]]);

  const getOptions = () => {
    if (parentValue) {
      const parentOption = mOptions.find(
        (option) => option.name === parentValue
      );
      if (parentOption?.options?.length > 0) {
        return parentOption.options;
      }

      return [];
    }
    return mOptions;
  };

  const itemsPerRow =
    theme.isLargeScreen || Platform.OS === "android"
      ? Math.round(containerWidth / (minWidth + SpaceType.Basic))
      : Math.floor(containerWidth / (minWidth + SpaceType.Basic));

  const columnsWidth = useMemo(() => {
    if (theme.isMobile) {
      return (
        (screenWidth -
          (2 * SpaceType.Double +
            2 * SpaceType.Double +
            columns * SpaceType.Basic) +
          columns -
          2) /
        columns
      );
    }
    const columnWidth = Math.floor(
      (containerWidth -
        (itemsPerRow - 1) * SpaceType.Basic -
        (fromResultScreen ? 0 : 2 * SpaceType.Double)) /
        itemsPerRow
    );
    return columnWidth;
  }, [itemsPerRow, containerWidth]);

  const rows = Math.min(
    displayedRows,
    Math.ceil(getOptions().length / itemsPerRow)
  );

  useEffect(() => {
    if (isTouched.current && parentFilter) {
      if (sectionName && payloadName) {
        dispatch(removeFiltersPayload({ sectionName, payloadName }));
        setSelectedItems([]);
      }
    } else {
      if (parentValue) isTouched.current = true;
    }
  }, [parentValue]);

  const filteredOptions = useMemo(() => {
    if (!itemsPerRow) return [];
    const initialOptions = getOptions();

    const numberOfItems = initialOptions.length;
    let numberOfPlaceholders = itemsPerRow - (numberOfItems % itemsPerRow);
    if (numberOfItems % itemsPerRow === 0) {
      numberOfPlaceholders = 0;
    }
    return [...initialOptions, ...Array(numberOfPlaceholders).fill({})];
  }, [parentValue, mOptions, itemsPerRow]);

  useEffect(() => {
    if (selectedItems.length > 0) {
      setShowAll(
        selectedItems.some(
          (item) => filteredOptions.indexOf(item) + 1 > rows * itemsPerRow
        )
      );
    }
  }, []);

  const handleItemSelection = (item: any, index: number) => {
    isTouched.current = true;

    if (selectionType === SelectionType.single) {
      if (selectedItems.includes(item)) {
        setSelectedItems([]);
      } else {
        setSelectedItems([item]);
      }
    } else if (selectionType === SelectionType.multiple) {
      setSelectedItems((prevSelected) =>
        prevSelected.includes(item)
          ? prevSelected.filter((prevItem) => prevItem !== item)
          : [...prevSelected, item]
      );
    } else if (selectionType === SelectionType.range) {
      if (startRangeIndex === null) {
        setStartRangeIndex(index);
        setSelectedItems([item]);
      } else if (startRangeIndex === index && selectedItems) {
        setSelectedItems([]);
        setStartRangeIndex(null);
      } else {
        const minIndex = Math.min(startRangeIndex, index);
        const maxIndex = Math.max(startRangeIndex, index);
        setSelectedItems(mOptions?.slice(minIndex, maxIndex + 1));
        setStartRangeIndex(null);
      }
    }
  };

  useEffect(() => {
    if (!isTouched.current) return;

    const filterKey = payloadName?.[0];
    if (selectedItems.length === 0 && sectionName && payloadName) {
      dispatch(removeFiltersPayload({ sectionName, payloadName }));
      if (filterKey && sectionName) {
        dispatch(resetExcludePayloadByFilterType({ sectionName, filterKey }));
      }
    } else {
      const payload: any = {};
      if (selectionType === SelectionType.range) {
        payload[payloadName[0]] = selectedItems[0]?.search_payload[0];
        payload[payloadName[1]] =
          selectedItems[selectedItems.length - 1]?.search_payload[0];
      } else if (
        selectionType === SelectionType.single &&
        !modifyShapeSupportForBuilder
      ) {
        payload[payloadName[0]] = selectedItems[0]?.search_payload[0];
      } else {
        payloadName?.forEach((name) => {
          payload[name] = selectedItems
            ?.map((item) => item?.search_payload)
            .flat();
        });
      }

      if (sectionName) {
        dispatch(setFiltersPayload({ sectionName, data: payload }));
      }

      if (filterKey && sectionName) {
        const excludeValues = selectedItems
          .filter((item) => item?.exclude_search_payload?.length > 0)
          .flatMap((item) => item.exclude_search_payload);

        if (excludeValues.length > 0) {
          dispatch(
            setExcludePayload({ sectionName, filterKey, values: excludeValues })
          );
        } else {
          dispatch(resetExcludePayloadByFilterType({ sectionName, filterKey }));
        }
      }
    }
  }, [selectedItems]);

  return parentFilter && !parentValue ? null : (
    <ParentView>
      {sectionTitle &&
        filteredOptions?.length > 0 &&
        filteredOptions?.filter((obj) => Object.keys(obj)?.length > 0).length >
          0 &&
        !isCollapsible && (
          <TitleSection>
            <VDBText isBold type={typography["H3-M"]} color={theme["core-05"]}>
              {sectionTitle}
            </VDBText>
            {selectedItems.length > 0 && (
              <SelectedItemsTitle
                selectionType={selectionType}
                selectedItems={selectedItems}
              />
            )}
          </TitleSection>
        )}
      <FilterView>
        {filteredOptions
          ?.slice(0, showAll ? filteredOptions?.length : rows * itemsPerRow)
          .map((option, index) => {
            const optionKey = safeKey(
              option?.payload_name?.[0] ||
                option?.search_payload?.[0] ||
                option?.name ||
                option?.label ||
                index
            );
            const optionTestId = `${baseTestId}.option.${optionKey}`;
            return (
              <Pressable
                onHoverIn={() => setHoveredIndex(index)}
                onHoverOut={() => setHoveredIndex(null)}
                key={index}
                onPress={() => {
                  if (option.name) handleItemSelection(option, index);
                }}
                disabled={
                  (isShapeFilter &&
                    enableJewelryBuilder &&
                    mEnterSecondFlow === true &&
                    !modifyShapeSupportForBuilder) ||
                  (modifyShapeSupportForBuilder &&
                    selectedItems[0]?.name === option?.name) ||
                  !option?.name
                }
                testID={optionTestId}
                data-testId={optionTestId}
              >
                <ImageTextFilterItem
                  key={index}
                  item={option}
                  screenWidth={containerWidth}
                  isSelected={selectedItems.some(
                    (item) => item === option || item?.name === option?.name
                  )}
                  selectedStyle={selectedStyle}
                  width={columnsWidth}
                  isHovered={index === hoveredIndex}
                />
              </Pressable>
            );
          })}
      </FilterView>
      <ShowMoreButton
        options={filteredOptions}
        columns={itemsPerRow}
        showAll={showAll}
        onPressFunction={setShowAll}
        displayedRows={rows}
        selectedProduct={selectedProduct}
      />
    </ParentView>
  );
};

const TitleSection = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-top: ${SpaceType.Extra}px;
  padding-bottom: ${SpaceType.Extra}px;
`;

const FilterView = styled.View`
  gap: ${SpaceType.Basic}px;
  justify-content: space-between;
  flex-direction: row;
  flex-wrap: wrap;
`;

const ParentView = styled.View`
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
`;

export default memo(ImageTextListSection);
